import { ThemeMode } from '@/hooks/useLightMode'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormMethods,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Flex, MultilineTextInput } from '@hivewatch/dark'

import { FormFileUploadControl } from '@/components/ui'
import { formatBytes } from '@/utils/formats'
import { tVar } from '@/utils/i18n'

import {
  ALLOWED_EXTENSIONS,
  ALLOWED_MIME_TYPES,
  MAX_FILENAME_LENGTH,
  MAX_FILE_SIZE,
} from '../constants/constants'

/** Kept in sync with the copy in `settings:feedback_form_message_max_length`. */
export const MAX_MESSAGE_LENGTH = 10000

export interface CreateFeedbackFormInputsIProps {
  message: string
  file?: FileList
}

interface CreateUserFeedbackFormIProps {
  colorScheme: ThemeMode
  control: Control<CreateFeedbackFormInputsIProps>
  errors: FieldErrors<CreateFeedbackFormInputsIProps>
  isDisabled: boolean
  onFileRemoved: () => void
  register: UseFormMethods<CreateFeedbackFormInputsIProps>['register']
}

/**
 * Field body for {@link CreateUserFeedbackModal}. The modal owns `useForm`,
 * submission and the footer buttons — this component only renders controls,
 * mirroring `DarkCreateFloorModal`.
 */
export const CreateUserFeedbackForm = ({
  colorScheme,
  control,
  errors,
  isDisabled,
  onFileRemoved,
  register,
}: CreateUserFeedbackFormIProps) => {
  const { t } = useTranslation()

  return (
    <Flex direction='column' gap='16px'>
      <Controller
        control={control}
        defaultValue=''
        name='message'
        render={({ onChange, value }) => (
          <MultilineTextInput
            colorScheme={colorScheme}
            data-testid='createUserFeedback_modal_message'
            hasSideBorders={false}
            hasWarning={!!errors.message}
            isDisabled={isDisabled}
            label={t('settings:feedback_form_message_label')}
            onChange={onChange}
            outerPadding='0'
            placeholder={t('settings:feedback_form_message_placeholder')}
            rightLabel={`${value.length}/${MAX_MESSAGE_LENGTH} ${t(
              'common:characters_used'
            )}`}
            rows={6}
            value={value}
            warningText={errors.message?.message}
          />
        )}
        rules={{
          required: t('settings:feedback_form_message_required'),
          maxLength: {
            value: MAX_MESSAGE_LENGTH,
            message: t('settings:feedback_form_message_max_length'),
          },
        }}
      />

      <FormFileUploadControl
        accept={ALLOWED_EXTENSIONS.join(',')}
        colorScheme={colorScheme}
        errorMessage={errors.file?.message}
        id='file'
        inputRef={register({
          validate: {
            fileSize: (fileList: FileList) => {
              if (!fileList || fileList.length === 0) return true
              return (
                fileList[0].size <= MAX_FILE_SIZE ||
                tVar(t, 'settings:feedback_form_file_size_error', {
                  maxSize: formatBytes(MAX_FILE_SIZE),
                })
              )
            },
            fileType: (fileList: FileList) => {
              if (!fileList || fileList.length === 0) return true
              return (
                ALLOWED_MIME_TYPES.includes(fileList[0].type) ||
                t('settings:feedback_form_file_type_error')
              )
            },
            fileName: (fileList: FileList) => {
              if (!fileList || fileList.length === 0) return true
              return (
                fileList[0].name.length <= MAX_FILENAME_LENGTH ||
                tVar(t, 'settings:feedback_form_file_name_error', {
                  maxLength: MAX_FILENAME_LENGTH,
                })
              )
            },
          },
        })}
        isDisabled={isDisabled}
        isInvalid={!!errors.file}
        label={t('settings:feedback_form_attachment_label')}
        onFileRemoved={onFileRemoved}
        placeholder={t('settings:feedback_form_attachment_placeholder')}
      />
    </Flex>
  )
}
